/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useCallback } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import MultiSelectWithCheckBox from '../../../../../components/FormElements/MultiSelectWithCheckBox'
import FormAutoComplete from '../../../../../components/FormElements/FormAutoComplete'
import FormInput from '../../../../../components/FormElements/FormInput'
import { BranchingOperation, type ILinearObject, type Question, SurveyItemQuestionType } from '../../../types'
import { getSurveySections } from '../../../../../store/slices/SurveyTemplateSlice/selectors'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { shade } from '../../../../../colors'
import { type ISurveySection } from '../../../../../store/slices/SurveyTemplateSlice'
import { BranchingOperationsDropdown } from '../../../constant'
import { findParentSectionAndQuestion, isOperationOptionDisabled } from '../../../helpers'

interface BranchModalProps {
  section: ISurveySection
}

const Branching: React.FC<BranchModalProps> = ({ section }) => {
  const sectionsList = getSurveySections()
  const { parentQuestion } = findParentSectionAndQuestion(sectionsList, section) ?? {}

  const { control, watch, setValue } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'branchingFilters'
  })

  const checkOperationValue = (index: number): boolean => {
    const operation = watch(`branchingFilters[${index}].operation`)?.value
    return operation !== BranchingOperation.Is
  }

  const handleKeyChange = useCallback((index: number, newValue: Record<string, any> | null) => {
    setValue(`branchingFilters[${index}].operation`, newValue)
    setValue(`branchingFilters[${index}].answers`, [])
  }, [setValue])

  const branchingOperationsDropdown = parentQuestion?.itemType === SurveyItemQuestionType.FREE_RESPONSE ? BranchingOperationsDropdown.filter(operation => operation.value !== BranchingOperation.Is) : BranchingOperationsDropdown

  const preparedAnswersOptions = parentQuestion?.object?.map((answer: string | ILinearObject) => typeof answer === 'string' ? answer : answer.option)

  return (
    <Box mt={4}>
      <Typography color='#000' fontWeight={'bold'} mb={2}>Survey Branching</Typography>

      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        border={`1px solid ${shade[650]}`}
        padding="16px"
        borderRadius="8px"
        bgcolor={shade[750]}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          {fields.map((item, index) => (
            <Grid container spacing={2} alignItems="center" key={item.id}>
              <Grid item xs={12} sm={5} md={2}>
                <Typography component={'div'} mt={'-28px'}>Display if response for</Typography>
              </Grid>
              <Grid item xs={12} sm={5} md={5}>
                <FormInput
                  defaultValue={section?.name ?? ''}
                  placeholder="Section title"
                  name={`branchingFilters[${index}].section`}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={3} md={5}>
                <FormInput
                  defaultValue={section?.branchingFilters?.prompt ?? ''}
                  placeholder="Question prompt"
                  name={`branchingFilters[${index}].question`}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <Controller
                  name={`branchingFilters[${index}].operation`}
                  control={control}
                  render={({ field }) => (
                    <FormAutoComplete
                      {...field}
                      options={branchingOperationsDropdown ?? []}
                      placeholder="Operations"
                      getOptionLabel={(option) => option?.title}
                      getOptionValue={(option) => option?.value}
                      required
                      onChange={(newValue) => { handleKeyChange(index, newValue) }}
                      getOptionDisabled={(operation) => isOperationOptionDisabled(operation, parentQuestion)}
                      sx={{
                        bgcolor: '#fff'
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={5}>
                <Controller
                  name={`branchingFilters[${index}].answers`}
                  control={control}
                  render={({ field }) => (
                    <MultiSelectWithCheckBox
                      {...field}
                      options={preparedAnswersOptions ?? []}
                      placeholder="Answers"
                      getOptionLabel={(option) => option}
                      getOptionValue={(option) => option}
                      fullWidth
                      required
                      disabled={checkOperationValue(index)}
                      sx={{
                        bgcolor: '#fff'
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default Branching
